import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "wouter";
import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";
import { AuthLayout } from "../components/shell.tsx";
import { Alert, Button, Panel, PasswordField, TextField } from "../components/ui.tsx";
import { ApiError, applyFieldErrors, post, setCsrfToken } from "../lib/api.ts";
import { ME_KEY } from "../lib/session.tsx";
import { passwordSchema, requestResetSchema, PASSWORD_HINT } from "../../shared/auth.ts";
import { usePageTitle } from "../lib/nav.tsx";

export function RequestResetPage() {
  usePageTitle("Recuperar acesso");
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<z.input<typeof requestResetSchema>>({ resolver: zodResolver(requestResetSchema), defaultValues: { email: "" } });

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const res = await post<{ data: { message: string } }>("/auth/request-reset", values);
      setSent(res.data.message);
    } catch (e) {
      applyFieldErrors(e, form.setError as never);
      setError(e instanceof ApiError ? e.message : "Não foi possível enviar agora.");
    }
  });

  return (
    <AuthLayout>
      <Panel>
        <h1 className="text-xl font-bold">Recuperar acesso</h1>
        <p className="mt-1 text-sm text-muted">Informe o e-mail da sua conta.</p>
        {error && <Alert tone="danger">{error}</Alert>}
        {sent ? (
          <Alert tone="success">{sent}</Alert>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            <TextField label="E-mail" type="email" autoComplete="username" autoFocus error={form.formState.errors.email?.message} {...form.register("email")} />
            <Button type="submit" className="mt-2 w-full" loading={form.formState.isSubmitting}>Enviar instruções</Button>
          </form>
        )}
        <p className="mt-4 text-sm">
          <Link href="/login" className="font-semibold text-primary">Voltar para a entrada</Link>
        </p>
      </Panel>
    </AuthLayout>
  );
}

const resetForm = z
  .object({ password: passwordSchema, confirm: z.string() })
  .refine((v) => v.password === v.confirm, { path: ["confirm"], message: "As senhas não conferem." });

export function ResetPasswordPage({ token }: { token: string }) {
  usePageTitle("Definir nova senha");
  const qc = useQueryClient();
  const [done, setDone] = useState(false);
  const [invalid, setInvalid] = useState<string | null>(null);
  const form = useForm<z.input<typeof resetForm>>({ resolver: zodResolver(resetForm), defaultValues: { password: "", confirm: "" } });

  const onSubmit = form.handleSubmit(async (values) => {
    setInvalid(null);
    try {
      await post("/auth/reset-password", { token, password: values.password });
      // Every session was revoked server-side, including one on this browser.
      setCsrfToken("");
      qc.clear();
      qc.setQueryData(ME_KEY, null);
      setDone(true);
    } catch (e) {
      if (e instanceof ApiError && e.code === "INVALID_RESET_TOKEN") setInvalid(e.message);
      else {
        applyFieldErrors(e, form.setError as never);
        if (!(e instanceof ApiError && e.fieldErrors.length)) setInvalid(e instanceof Error ? e.message : "Não foi possível salvar.");
      }
    }
  });

  return (
    <AuthLayout>
      <Panel>
        <h1 className="text-xl font-bold">Definir nova senha</h1>
        {done ? (
          <>
            <Alert tone="success">Senha redefinida. Entre novamente com a nova senha.</Alert>
            <Link href="/login" className="inline-flex rounded-ctl bg-primary px-4 py-2 text-sm font-semibold text-white">Ir para a entrada</Link>
          </>
        ) : invalid ? (
          <>
            <Alert tone="warn">{invalid}</Alert>
            <Link href="/recuperar" className="text-sm font-semibold text-primary">Solicitar novo link</Link>
          </>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            <PasswordField label="Nova senha" autoComplete="new-password" hint={PASSWORD_HINT} error={form.formState.errors.password?.message} {...form.register("password")} />
            <PasswordField label="Confirmar nova senha" autoComplete="new-password" error={form.formState.errors.confirm?.message} {...form.register("confirm")} />
            <Button type="submit" className="mt-2 w-full" loading={form.formState.isSubmitting}>Salvar nova senha</Button>
            <p className="mt-3 text-xs text-muted">Ao concluir, entre novamente. Suas sessões anteriores serão encerradas.</p>
          </form>
        )}
      </Panel>
    </AuthLayout>
  );
}
